/**
 * Alergénios no painel (#325): estado de um produto e seleção inicial do
 * editor. Lógica pura (testada em allergens.test.ts); a UI fica em
 * components/AllergensEditor.tsx.
 *
 * Regra: só a lista CONFIRMADA pelo dono é facto. Enquanto não há
 * confirmação, o editor parte das sugestões da IA (pré-marcadas) para o dono
 * conferir e confirmar com um clique.
 */
import {
  EU_ALLERGEN_CODES,
  allergenKey,
  allergenName,
  type AllergenCode,
} from '@self-restaurant/shared'
import type { ImportDraftProduct, Product, ProductAllergen } from './api.js'

export type AllergenState = 'confirmed' | 'pending' | 'unreviewed'

/**
 * confirmed = o dono confirmou a lista (mesmo vazia: "sem alergénios");
 * pending = há sugestões por confirmar; unreviewed = ninguém olhou ainda
 * (nada sugerido). NÃO é "sem alergénios": o editor mostra "a revisar"
 * para não parecer confirmado com zero (QA da #331).
 */
export function allergenState(
  p: Pick<Product, 'allergens' | 'allergens_confirmed_at'>,
): AllergenState {
  if (p.allergens_confirmed_at) return 'confirmed'
  return (p.allergens ?? []).some((a) => a.status === 'suggested') ? 'pending' : 'unreviewed'
}

/**
 * Selo de estado dos alergénios, igual no catálogo (cartão), no editor do
 * produto e na revisão do import (QA da #340): os mesmos 4 estados. O
 * "confirmado" divide-se em "sem alergénios" (lista vazia confirmada) e
 * "confirmados" (com alergénios).
 */
export type AllergenBadgeState = 'unreviewed' | 'pending' | 'none' | 'confirmed'

/** Estado de 3 (editor/revisão) + nº de alergénios confirmados → selo de 4. */
export function badgeStateOf(state: AllergenState, confirmedCount: number): AllergenBadgeState {
  if (state !== 'confirmed') return state
  return confirmedCount > 0 ? 'confirmed' : 'none'
}

/** Selo de um produto do catálogo — o mesmo no cartão e no editor do produto. */
export function allergenBadgeState(
  p: Pick<Product, 'allergens' | 'allergens_confirmed_at'>,
): AllergenBadgeState {
  const confirmed = (p.allergens ?? []).filter((a) => a.status === 'confirmed').length
  return badgeStateOf(allergenState(p), confirmed)
}

export type AllergenBadgeColor = 'gray' | 'yellow' | 'blue' | 'green'

/**
 * Texto (chave i18n do editor), cor e ícone de cada estado. O texto distingue
 * sozinho os 4 estados (nunca só a cor); o ícone é decorativo (aria-hidden).
 */
export const ALLERGEN_BADGE: Record<
  AllergenBadgeState,
  { labelKey: string; color: AllergenBadgeColor; icon: string }
> = {
  unreviewed: { labelKey: 'admin.menu.allergens_unreviewed', color: 'gray', icon: '?' },
  pending: { labelKey: 'admin.menu.allergens_unconfirmed', color: 'yellow', icon: '!' },
  none: { labelKey: 'admin.menu.allergens_none', color: 'blue', icon: '∅' },
  confirmed: { labelKey: 'admin.menu.allergens_confirmed', color: 'green', icon: '✓' },
}

/**
 * Texto do selo: "Sem alergénios" já diz do que se trata; os outros levam o
 * prefixo "Alergénios: …". `prefix: false` (notas do QA da #340): no editor do
 * produto e na revisão do import o selo já está ao lado do rótulo
 * «Alergénios» — sem o prefixo para não repetir; o nome acessível completo
 * vai no title e o grupo é rotulado pelo próprio rótulo (aria-labelledby).
 */
export function allergenBadgeText(
  t: (key: string) => string,
  state: AllergenBadgeState,
  opts: { prefix?: boolean } = {},
): string {
  const label = t(ALLERGEN_BADGE[state].labelKey)
  if (state === 'none' || opts.prefix === false) return label
  return `${t('admin.menu.allergens')}: ${label}`
}

/**
 * Classes do selo: ícone e texto separados por `gap` (lógico, vale em LTR e
 * RTL) — antes era `mr-1` no ícone, que em árabe ficava do lado errado e
 * colava o ícone ao texto (notas do QA da #340). Sem margens left/right.
 */
export const ALLERGEN_BADGE_CLASS = 'text-xs gap-1'

/**
 * Lista de alergénios no idioma do painel (notas do QA da #340): em árabe a
 * vírgula latina ", " ficava errada. Intl.ListFormat (conjunção) dá o
 * separador do idioma; locale inválido ou sem suporte → ", ".
 */
export function formatAllergenList(names: readonly string[], lang: string | undefined): string {
  try {
    return new Intl.ListFormat(lang, { type: 'conjunction', style: 'long' }).format(names)
  } catch {
    return names.join(', ')
  }
}

/** Códigos pré-marcados no editor: os confirmados ou, sem confirmação, os sugeridos. */
export function initialSelection(
  p: Pick<Product, 'allergens' | 'allergens_confirmed_at'>,
): number[] {
  const rows = p.allergens ?? []
  const wanted = p.allergens_confirmed_at
    ? rows.filter((a) => a.status === 'confirmed')
    : rows.filter((a) => a.status === 'suggested')
  return [...new Set(wanted.map((a) => a.allergen))].sort((a, b) => a - b)
}

/** Sugestão da IA para um código (para marcar o chip com a origem). */
export function suggestionOf(
  p: Pick<Product, 'allergens' | 'allergens_confirmed_at'>,
  code: number,
): ProductAllergen | undefined {
  if (p.allergens_confirmed_at) return undefined
  return (p.allergens ?? []).find((a) => a.allergen === code && a.status === 'suggested')
}

/** Origem que o chip mostra: ▣ impresso, ✦ IA (o 'owner' de uma confirmação reaberta não tem marca). */
export function chipSource(
  source: ProductAllergen['source'] | undefined,
): 'menu' | 'ai' | undefined {
  return source === 'menu' || source === 'ai' ? source : undefined
}

export function toggleCode(selected: number[], code: number): number[] {
  return selected.includes(code)
    ? selected.filter((c) => c !== code)
    : [...selected, code].sort((a, b) => a - b)
}

/** A seleção atual difere da lista já confirmada? (sem confirmação: sempre há o que confirmar) */
export function hasChanges(
  p: Pick<Product, 'allergens' | 'allergens_confirmed_at'>,
  selected: number[],
): boolean {
  if (!p.allergens_confirmed_at) return true
  return initialSelection(p).join(',') !== [...selected].sort((a, b) => a - b).join(',')
}

/**
 * Nome do alergénio: dicionário do banco (ui_texts `allergen.<n>`, editável)
 * e, sem ele, o nome oficial embutido em @self-restaurant/shared.
 */
export function allergenLabel(
  t: (key: string) => string,
  code: AllergenCode,
  lang: string,
): string {
  const key = allergenKey(code)
  const v = t(key)
  return v && v !== key ? v : allergenName(code, lang)
}

// ─── Revisão do import (#325, PR 2) ──────────────────────────────────────────
// Na revisão o produto ainda não existe: as decisões ficam no rascunho
// (allergens_confirmed / ingredients_accepted / ingredients_rejected) e o
// commit grava-as. Mesmos estados e chips do editor do produto.

type DraftAllergens = Pick<ImportDraftProduct, 'allergens' | 'allergens_confirmed'>

export function draftAllergenState(p: DraftAllergens): AllergenState {
  if (Array.isArray(p.allergens_confirmed)) return 'confirmed'
  return (p.allergens ?? []).length > 0 ? 'pending' : 'unreviewed'
}

/** Selo de um produto do rascunho (revisão do import): mesmos 4 estados do catálogo. */
export function draftAllergenBadgeState(p: DraftAllergens): AllergenBadgeState {
  return badgeStateOf(draftAllergenState(p), (p.allergens_confirmed ?? []).length)
}

/** Seleção do editor na revisão: a lista confirmada ou, sem ela, as sugestões. */
export function draftSelection(p: DraftAllergens): number[] {
  const codes = Array.isArray(p.allergens_confirmed)
    ? p.allergens_confirmed
    : (p.allergens ?? []).map((a) => a.code)
  return [...new Set(codes)].sort((a, b) => a - b)
}

/** Origem da sugestão de um código no rascunho (▣ impresso / ✦ IA). */
export function draftSourceOf(p: DraftAllergens, code: number): 'menu' | 'ai' | undefined {
  return (p.allergens ?? []).find((a) => a.code === code)?.source
}

/**
 * Chave estável de um produto do rascunho entre rondas de revisão: o rid
 * do cliente muda quando o rascunho do servidor é adotado; categoria + nome
 * (com trim) é o mesmo identificador que a revisão envia ao servidor.
 */
export function draftKey(category: string, name: string): string {
  return `${category.trim()}\u0000${name.trim()}`
}

/** Decisões do dono sobre um produto do rascunho (sobrevivem à ressincronização). */
export type DraftDecision = {
  allergens?: number[]
  accepted?: string[]
  rejected?: string[]
}

/** Aplica as decisões guardadas a um produto do rascunho (para mostrar e para o commit). */
export function withDecision<T extends ImportDraftProduct>(p: T, d: DraftDecision | undefined): T {
  if (!d) return p
  const names = new Set((p.ingredient_suggestions ?? []).map((s) => s.name))
  const out: T = { ...p }
  if (d.allergens) out.allergens_confirmed = [...d.allergens].sort((a, b) => a - b)
  const accepted = (d.accepted ?? []).filter((n) => names.has(n))
  const rejected = (d.rejected ?? []).filter((n) => names.has(n))
  if (accepted.length > 0) out.ingredients_accepted = accepted
  if (rejected.length > 0) out.ingredients_rejected = rejected
  return out
}

/** Ingrediente provável: pendente → aceite → descartado → pendente. */
export function cycleIngredient(d: DraftDecision | undefined, name: string): DraftDecision {
  const accepted = new Set(d?.accepted ?? [])
  const rejected = new Set(d?.rejected ?? [])
  if (accepted.has(name)) {
    accepted.delete(name)
    rejected.add(name)
  } else if (rejected.has(name)) {
    rejected.delete(name)
  } else {
    accepted.add(name)
  }
  return { ...d, accepted: [...accepted], rejected: [...rejected] }
}

export function ingredientDecision(
  d: DraftDecision | undefined,
  name: string,
): 'accepted' | 'rejected' | 'pending' {
  if (d?.accepted?.includes(name)) return 'accepted'
  if (d?.rejected?.includes(name)) return 'rejected'
  return 'pending'
}

export { EU_ALLERGEN_CODES }
